import { trpc } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { reportSearchDeps, reportQueryOptions, PANES_WITH_OWN_QUERY } from '@/hooks/use-report'
import { noop } from '@tanstack/react-query'
import { orgGroupDetail } from './-components/orgs/org-group-detail'

/**
 * Data-owning layout for a selected functional group (`/orgs/$orgId/groups/$groupId/*`): the orgId/groupId-keyed
 * chrome its subtree reads — `getGroups`, `getOrgGroupUsers` (read by the users panel in orgs.tsx) — and the report
 * data shared by all three tab children. NO component, deliberately: with nothing to code-split there is no chunk
 * for the router to await before the leaf's skeleton can paint, and a re-key on the report search paints nothing.
 */
export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId')({
  staticData: { detailPanes: orgGroupDetail },
  loaderDeps: ({ search }) => reportSearchDeps(search),
  context: ({ deps, params }) => ({
    reportQueryOptions: reportQueryOptions(deps, params),
    // The board/distribution panes' profile, owned HERE so an ámbito switch re-keys THIS match rather than
    // a leaf's: a leaf at `pendingMs: 0` paints its skeleton the moment its match reloads, and this layout has
    // no pendingComponent at all. Only the PREFETCH is gated on the pane below (see the loader) — the key
    // still lives here, so both readers get the one object.
    orgGroupProfileQueryOptions: trpc.orgs.getOrgGroupProfile.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
    // Both are read ABOVE this route (the groups breadcrumb + group name in orgs.tsx, the users panel it
    // renders), but only this route has the ids — so it owns the prefetch and everyone shares the factory.
    orgGroupsQueryOptions: trpc.orgs.getGroups.queryOptions(params.orgId),
    orgGroupUsersQueryOptions: trpc.orgs.getOrgGroupUsers.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
  }),
  loader: ({ context, location }) => {
    // The panes orgs.tsx renders for a selected group: a row's hover fetches their chunk with the data.
    orgGroupDetail.preload().catch(noop)
    context.queryClient.query(context.orgGroupsQueryOptions).catch(noop)
    context.queryClient.query(context.orgGroupUsersQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
    // Board and distribution are the ONLY readers, and this layout also sits under /report and the index —
    // where warming it is a request per visit that nothing consumes.
    if (PANES_WITH_OWN_QUERY.test(location.pathname)) {
      context.queryClient.query(context.orgGroupProfileQueryOptions).catch(noop)
    }
  },
})
